import { debounce } from 'radash';
import { type Ref, ref, watch } from 'vue';

import MeService from '@/core/shared/me/me.service';
import { ProjectPersistence } from '@/core/shared/project/project.persistence';
import type { ProjectSearchQuery } from '@/core/shared/project/project.type';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';

import type { Pagination, Project } from '@factoryfixinc/ats-interfaces';
import { ProjectSource } from '@factoryfixinc/ats-interfaces';

type Options = {
  getExcludedJobIds?: () => number[] | undefined;
  // Seed the filter state at creation. Because these set the refs' initial values
  // (before the watcher below is registered), scoping the selector this way does not
  // trip the filter watcher — so a single explicit fetchProjects() runs exactly one
  // request, rather than the watcher firing a second, redundant one.
  initialFilters?: {
    myJobs?: boolean;
    copilot?: boolean;
    ats?: boolean;
  };
  // Opt-in: ask the backend to sort active (copilot) jobs first. Shared callers that
  // don't pass this keep their existing unsorted-by-priority behavior.
  prioritizeCopilot?: boolean;
};

export type UseProjectSelectorReturn = {
  projectSearch: Ref<string>;
  myJobsFilter: Ref<boolean>;
  copilotJobsFilter: Ref<boolean>;
  atsJobsFilter: Ref<boolean>;
  projects: Ref<Project[]>;
  isLoadingProjects: Ref<boolean>;
  fetchProjects: () => Promise<void>;
  renderProject: (project?: Record<string, unknown>) => string;
  resetFilters: () => void;
};

export function useProjectSelector(options?: Options): UseProjectSelectorReturn {
  const meService = new MeService();
  const projectPersistence = new ProjectPersistence();

  // Filters and search state
  const projectSearch = ref('');
  const myJobsFilter = ref(options?.initialFilters?.myJobs ?? false);
  const copilotJobsFilter = ref(options?.initialFilters?.copilot ?? false);
  const atsJobsFilter = ref(options?.initialFilters?.ats ?? false);

  // Data state
  const projects = ref<Project[]>([]);
  const isLoadingProjects = ref(false);

  function buildSearchQuery(): ProjectSearchQuery {
    const searchQuery: ProjectSearchQuery = {};

    if (options?.prioritizeCopilot) {
      searchQuery.prioritizeCopilot = true;
    }

    if (myJobsFilter.value && meService.userProfile) {
      searchQuery.teamIds = [meService.userProfile.id];
    }

    if (copilotJobsFilter.value) {
      searchQuery.copilot = true;
    }

    if (atsJobsFilter.value) {
      searchQuery.source = ProjectSource.ATS;
    }

    if (projectSearch.value.trim().length > 0) {
      searchQuery.titleOrReqId = projectSearch.value.trim();
    }

    const pagination: Pagination = { page: 1, itemsPerPage: 100 };
    searchQuery.pagination = pagination;

    return searchQuery;
  }

  function filterProjects(list?: Project[]): Project[] {
    if (!list) return [];
    const excluded = options?.getExcludedJobIds?.();
    if (excluded && excluded.length > 0) {
      return list.filter((p) => !excluded.includes(p.jobId));
    }
    return list;
  }

  async function fetchProjects() {
    try {
      isLoadingProjects.value = true;
      const result = await projectPersistence.searchProjects({
        employerId: meService.employer?.id as number,
        search: buildSearchQuery(),
      });
      projects.value = filterProjects(result.projects);
    } catch (error) {
      ErrorService.captureException(error);
      SnackbarService.critical('Failed to load projects. Please try again.');
    } finally {
      isLoadingProjects.value = false;
    }
  }

  function renderProject(project?: Record<string, unknown>) {
    let title = '';
    if (!project) return '';
    if (project.title) title = `${project.title}`;
    if (project.location) title += ` • ${project.location}`;
    if (project.jobId) title += ` | #${project.jobId}`;

    let copilotStatus = '';
    let copilotStatusColor = '';
    if (project.copilot) {
      copilotStatus = 'Job active';
      copilotStatusColor = 'text-highlight-500';
      if (project.remoteJobId) copilotStatus = 'Job active (ATS)';
    } else {
      copilotStatus = 'Job inactive';
      copilotStatusColor = 'text-shade-500';
      if (project.remoteJobId) copilotStatus = 'Job inactive (ATS)';
    }
    return `
      <div class="flex items-center justify-between">
        <span class="truncate text-sm">${title}</span>
        <span class="${copilotStatusColor} text-xs">${copilotStatus}</span>
      </div>
    `;
  }

  function resetFilters() {
    projectSearch.value = '';
    myJobsFilter.value = false;
    copilotJobsFilter.value = false;
    atsJobsFilter.value = false;
  }

  const debouncedFetchProjects = debounce({ delay: 400 }, fetchProjects);

  // Auto-refresh on filter/search changes
  watch([myJobsFilter, copilotJobsFilter, atsJobsFilter], () => {
    fetchProjects();
  });
  watch(projectSearch, () => {
    debouncedFetchProjects();
  });

  return {
    // state
    projectSearch,
    myJobsFilter,
    copilotJobsFilter,
    atsJobsFilter,
    projects,
    isLoadingProjects,

    // methods
    fetchProjects,
    renderProject,
    resetFilters,
  };
}
